// src/shared/ingredientFormat.ts
// Ingredient normalization utilities using existing culinary.ts functions

import { 
  normalizeUnit, 
  toPrettyFraction, 
  formatQty,
  normalizeIngredientDisplay,
  canonicalizeIngredientName
} from './culinary';

/**
 * Parse mixed fractions like "1 1/2" or simple fractions like "1/2"
 */
function parseMixedOrFraction(s: string): number | null {
  s = s.trim();
  
  // Handle mixed fractions: "1 1/2"
  const mixedMatch = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixedMatch) {
    const whole = parseInt(mixedMatch[1]);
    const numerator = parseInt(mixedMatch[2]);
    const denominator = parseInt(mixedMatch[3]);
    return whole + (numerator / denominator);
  }
  
  // Handle simple fractions: "1/2"
  const fracMatch = s.match(/^(\d+)\/(\d+)$/);
  if (fracMatch) {
    const numerator = parseInt(fracMatch[1]);
    const denominator = parseInt(fracMatch[2]);
    return numerator / denominator;
  }
  
  // Handle decimals: "0.5" or integers: "2"
  const num = Number(s);
  return Number.isFinite(num) ? num : null;
}

/**
 * Normalize a free-text ingredient line using existing culinary utilities
 * Examples:
 * - "3 Tbs nonfat milk" → "3 tbsp nonfat milk"
 * - "0.5 tsp butter extract" → "½ tsp butter extract"
 * - "1 1/2 cups rolled oats" → "1½ cups rolled oats"
 */
export function normalizeIngredientLine(line: string): string {
  const s = line.trim().replace(/\s+/g, ' ');
  
  // Try to match: amount + optional unit + rest of ingredient
  // Amount can be: number, decimal, fraction, or mixed fraction
  const match = s.match(
    /^([0-9]+(?:\.[0-9]+)?|\d+\s+\d+\/\d+|\d+\/\d+)\s*([a-zA-Z]+)?\b(.*)$/
  );
  
  if (!match) {
    // No amount/unit found, just normalize the name
    const { name, notesExtra } = normalizeIngredientDisplay(s);
    const canonicalName = canonicalizeIngredientName(name);
    return notesExtra ? `${canonicalName} ${notesExtra}` : canonicalName;
  }

  const amountRaw = match[1];
  const unitRaw = (match[2] || '').toLowerCase();
  const rest = match[3].trim();

  // Parse the amount (handles fractions, decimals, mixed numbers)
  const amountNum = parseMixedOrFraction(amountRaw);
  
  // Normalize the unit using existing culinary utilities
  const unit = normalizeUnit(unitRaw);
  
  // Format the amount (converts decimals to pretty fractions)
  const amountFormatted = amountNum !== null ? toPrettyFraction(amountNum) : amountRaw;
  
  // Normalize the ingredient name and separate notes
  const { name, notesExtra } = normalizeIngredientDisplay(rest);
  const canonicalName = canonicalizeIngredientName(name);
  
  // Combine parts
  const parts = [amountFormatted, unit, canonicalName].filter(Boolean);
  const result = parts.join(' ');
  
  // Add notes if present
  return notesExtra ? `${result} ${notesExtra}` : result;
}

/**
 * Normalize a structured ingredient object (ScalerIngredient format)
 */
export function formatStructuredIngredient(ing: { 
  amount?: number | string; 
  unit?: string; 
  name: string; 
  note?: string;
  raw?: string;
}): string {
  // Use raw if available and structured data is incomplete
  if (ing.raw && (!ing.amount || !ing.unit)) {
    return normalizeIngredientLine(ing.raw);
  }

  // Parse amount if it's a string
  let amountNum: number | null = null;
  if (typeof ing.amount === 'string') {
    amountNum = parseMixedOrFraction(ing.amount);
  } else if (typeof ing.amount === 'number') {
    amountNum = ing.amount;
  }

  // Normalize unit
  const unit = normalizeUnit(ing.unit);
  
  // Format quantity with unit
  let quantityPart = '';
  if (amountNum !== null && unit) {
    quantityPart = formatQty(amountNum, unit);
  } else if (amountNum !== null) {
    quantityPart = toPrettyFraction(amountNum);
  } else if (ing.amount) {
    quantityPart = String(ing.amount);
  }

  // Normalize name
  const { name, notesExtra } = normalizeIngredientDisplay(ing.name);
  const canonicalName = canonicalizeIngredientName(name);
  
  // Combine parts
  const parts = [quantityPart, canonicalName].filter(Boolean);
  let result = parts.join(' ');
  
  // Add preparation notes
  if (notesExtra) {
    result += ` ${notesExtra}`;
  }
  
  // Add additional notes
  if (ing.note && ing.note.trim()) {
    result += `, ${ing.note.trim()}`;
  }
  
  return result.trim();
}
